import * as React from 'react';
import styled from 'styled-components';

import { color } from '@creditornot/cb-ingredients/design-tokens';
import { MenuAccordionCheckboxItem, MenuCheckboxItem } from '@creditornot/cb-menu';

import { ListItemBase } from './types';
import {
  getAllCountryCodesForItems,
  getCountriesBySelectedItems,
  itemsByCountry,
  uniqueCodesFromItems,
} from './utils';
import { CountryCode } from '../../../../api-types/common';
import { LocalizedCountry } from '../../LocalizedCountry';
import { Typography as T } from '../../Typography/Typography';

const ListItemName = styled(T.footnote)`
  color: ${color.textSubdued};
  display: block;
`;

type Props<ListItem extends ListItemBase> = {
  openAllCountries?: boolean;
  items: ListItem[];
  filterItems: string[];
  onChange: (items: string[]) => void;
};

export const FilterItems = <ListItem extends ListItemBase>({
  openAllCountries = false,
  items,
  filterItems,
  onChange,
}: React.PropsWithChildren<Props<ListItem>>) => {
  const [openCountries, setOpenCountries] = React.useState<string[]>(
    openAllCountries
      ? getAllCountryCodesForItems(items)
      : getCountriesBySelectedItems(filterItems, items),
  );

  React.useEffect(() => {
    if (openAllCountries) {
      setOpenCountries(getAllCountryCodesForItems(items));
    }
    if (!openAllCountries) {
      const countriesBySelectedItems = getCountriesBySelectedItems(filterItems, items);
      if (countriesBySelectedItems.length > 0) {
        setOpenCountries((prevOpenCountries) => [
          ...prevOpenCountries,
          ...countriesBySelectedItems,
        ]);
      }
    }
  }, [items, filterItems, openAllCountries]);

  const toggleCountryOpen = (countryCode: string /* TODO api specs */) => {
    if (openCountries.includes(countryCode)) {
      setOpenCountries((prevOpenCountries) => prevOpenCountries.filter((c) => c !== countryCode));
    } else {
      setOpenCountries((prevOpenCountries) => [...prevOpenCountries, countryCode]);
    }
  };

  const toggleCountryItems = (countryItems: ListItem[]) => {
    const allChecked = countryItems.every(({ code }) => filterItems.includes(code));
    if (allChecked) {
      onChange(
        filterItems.filter((code) => countryItems.find((v) => v.code === code) === undefined),
      );
    } else {
      onChange([...filterItems, ...uniqueCodesFromItems(filterItems, countryItems)]);
    }
  };

  const toggleFilterItemSelection = (code: string) => {
    if (filterItems.includes(code)) {
      onChange(filterItems.filter((v) => v !== code));
    } else {
      onChange([...filterItems, code]);
    }
  };

  return (
    <>
      {itemsByCountry<ListItem>(items).map(({ countryCode, items: countryItems }) => (
        <MenuAccordionCheckboxItem
          key={countryCode}
          open={openCountries.includes(countryCode)}
          onClick={() => toggleCountryOpen(countryCode)}
          accordionContent={
            <>
              {countryItems.map(({ name, code }) => (
                <MenuCheckboxItem
                  key={code}
                  checked={filterItems.includes(code)}
                  onClick={() => toggleFilterItemSelection(code)}
                >
                  <div>{code}</div>
                  <ListItemName title={name}>{name}</ListItemName>
                </MenuCheckboxItem>
              ))}
            </>
          }
          checkboxProps={{
            checked: countryItems.every(({ code }) => filterItems.includes(code)),
            partiallyChecked: countryItems.some(({ code }) => filterItems.includes(code)),
            onChange: () => toggleCountryItems(countryItems),
          }}
        >
          <LocalizedCountry countryCode={countryCode as CountryCode /* TODO api specs */} />
        </MenuAccordionCheckboxItem>
      ))}
    </>
  );
};
